import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useCallback } from 'react';
import { ModalWindow, Input, TextArea, useLocalizeMessage, FormSections } from 'libs.nucleus';
export const CloneMeasureModal = ({ isOpen, onClose, onSave, dataView, sourceMeasureId, isLoading = false, }) => {
    const translate = useLocalizeMessage();
    const sourceMeasure = dataView.data?.measures?.[sourceMeasureId];
    const originalTitle = sourceMeasure?.title || sourceMeasureId;
    const [name, setName] = useState(`Copy of ${originalTitle}`);
    const [description, setDescription] = useState(sourceMeasure?.description || '');
    const normalizeTitle = useCallback((value) => value.trim().toLowerCase(), []);
    const isDuplicateTitle = useMemo(() => {
        const candidate = name?.trim();
        if (!candidate) {
            return false;
        }
        const normalizedCandidate = normalizeTitle(candidate);
        const dimensions = dataView.data?.dimensions || {};
        const measures = dataView.data?.measures || {};
        const dimensionTitleExists = Object.values(dimensions).some((dimension) => {
            const title = (dimension.title || '').trim();
            return !!title && normalizeTitle(title) === normalizedCandidate;
        });
        if (dimensionTitleExists) {
            return true;
        }
        return Object.values(measures).some((measure) => {
            const title = (measure.title || '').trim();
            return !!title && normalizeTitle(title) === normalizedCandidate;
        });
    }, [name, dataView.data?.dimensions, dataView.data?.measures, normalizeTitle]);
    const nameError = useMemo(() => {
        if (!name?.trim() || !isDuplicateTitle) {
            return '';
        }
        return translate('A dimension or measure with this name already exists');
    }, [name, isDuplicateTitle, translate]);
    const isValid = useMemo(() => {
        return !!name?.trim() && !isDuplicateTitle;
    }, [name, isDuplicateTitle]);
    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);
    const handleSave = useCallback(() => {
        if (!isValid) {
            return;
        }
        onSave(name.trim(), description.trim());
    }, [isValid, name, description, onSave]);
    const formSections = [
        {
            title: translate('Details'),
            content: (_jsxs("div", { className: 'flex flex-col gap-6 max-w-input-lg', children: [_jsx(Input, { id: 'clone-measure-name', label: translate('Name'), value: name, onChange: (e) => setName(e.target.value), hasError: !!nameError, errorMessage: nameError, required: true, width: 'full', autoComplete: 'off' }), _jsx(TextArea, { id: 'clone-measure-description', label: translate('Description'), value: description, onChange: (e) => setDescription(e.target.value), width: 'full' })] })),
        },
    ];
    if (!isOpen) {
        return null;
    }
    return (_jsx(ModalWindow, { title: translate('Clone measure'), subtitle: dataView.name, isOpen: true, closeWindow: handleClose, footerPrimaryActionButton: {
            label: translate('Save'),
            onClick: handleSave,
            disabled: isLoading || !isValid,
        }, footerSecondaryActionButtons: [
            {
                label: translate('Cancel'),
                onClick: handleClose,
            },
        ], children: _jsx("div", { className: 'flex flex-col size-full', children: _jsx(FormSections, { sections: formSections }) }) }));
};
